// src/components/common/AppDatePicker.jsx
// Drop-in replacement for <TextField type="date" />.
//
// The native input renders differently in every browser and always follows the
// browser locale (a Greek user was shown mm/dd/yyyy). This wraps MUI X
// DatePicker — the free, MIT-licensed package, not the Pro range pickers —
// behind the exact contract the native input had:
//
//   value    : "YYYY-MM-DD" string (or "" when empty)
//   onChange : called with a "YYYY-MM-DD" string (or "" when cleared)
//
// Keeping the string contract means call sites keep their existing state and
// handlers; only the element changes.
//
// Presentation follows the convention used by European energy-market tools
// (Nord Pool, ENTSO-E and friends), which is what our users read all day:
//   - day-month-year dates (01-08-2026), read the same way across Europe
//   - Monday-first weeks with ISO week numbers, the unit trading desks plan in
//   - weekends picked out in red
import { useState } from "react";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { LocalizationProvider, DatePicker, PickersDay, PickersLayout } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import enGB from "date-fns/locale/en-GB";
import el from "date-fns/locale/el";
import { addMonths, addYears, format, getDay, getYear, isValid, parseISO, setYear } from "date-fns";
import { useLanguage } from "../../context/LanguageContext";

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  const parsed = parseISO(String(value));
  return isValid(parsed) ? parsed : null;
};

const toStr = (date) => (date && isValid(date) ? format(date, "yyyy-MM-dd") : "");

// Saturday/Sunday in red, and square-ish cells rather than circles. The
// :not(.Mui-selected) guard keeps the selected day's white-on-blue readable.
function MarketDay(props) {
  const { day, sx, ...other } = props;
  const weekend = getDay(day) === 0 || getDay(day) === 6;
  return (
    <PickersDay
      {...other}
      day={day}
      sx={{
        borderRadius: "6px",
        "&:hover, &.Mui-focusVisible": {
          backgroundColor: (t) => alpha(t.palette.primary.main, 0.18),
        },
        // Keep the selected day unmistakably selected while hovering it.
        "&.Mui-selected:hover": { backgroundColor: "primary.dark" },
        ...(weekend ? { "&:not(.Mui-selected)": { color: "error.main" } } : null),
        ...sx,
      }}
    />
  );
}

// MUI's stock header always reads "September 2026" and hides its arrows once
// you leave the day view. This one relabels per view — "September 2026" ->
// "2026" -> the year range — and keeps the arrows meaningful in each, stepping
// a month, a year, or a page of years. It also owns the corner layout, so no
// CSS surgery on MUI's internals is needed.
function CalendarHeader({
  currentMonth,
  onMonthChange,
  view,
  onViewChange,
  disabled,
  onShiftYears,
  onOpenYearGrid,
}) {
  const { isGreek, t } = useLanguage();
  const step = (direction) => {
    if (view === "day") {
      onMonthChange(addMonths(currentMonth, direction));
      return;
    }
    // MonthCalendar and YearCalendar select from `value`, not from the header's
    // currentMonth (see DateCalendar: they receive value={value}). Moving only
    // currentMonth therefore relabelled the header but left the year behind, so
    // picking a month applied the new month to the OLD year. Shift the value
    // itself — which is also what MUI does when a year is picked — and keep
    // currentMonth in step so the day grid lands on the right month.
    const years = direction;
    onShiftYears?.(years);
    onMonthChange(addYears(currentMonth, years));
  };

  const label =
    view === "day"
      ? format(currentMonth, "MMMM yyyy", { locale: isGreek ? el : enGB })
      : format(currentMonth, "yyyy");

  // day -> month -> (our year grid, via onOpenYearGrid)
  const nextView = view === "day" ? "month" : "day";

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ px: 1, pt: 1, pb: 0.5, minHeight: 40 }}
    >
      <IconButton size="small" disabled={disabled} onClick={() => step(-1)} aria-label={t("date.previous")}>
        <ChevronLeftIcon fontSize="small" />
      </IconButton>

      <Stack
        direction="row"
        alignItems="center"
        spacing={0.25}
        onClick={() => {
          if (disabled) return;
          if (view === "month") onOpenYearGrid?.();
          else onViewChange(nextView);
        }}
        sx={{ cursor: disabled ? "default" : "pointer", userSelect: "none" }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <ArrowDropDownIcon
          fontSize="small"
          sx={{
            transition: "transform 150ms",
            transform: view === "day" ? "none" : "rotate(180deg)",
          }}
        />
      </Stack>

      <IconButton size="small" disabled={disabled} onClick={() => step(1)} aria-label={t("date.next")}>
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}

const YEARS_PER_PAGE = 12;
const pageStartFor = (year) => Math.floor(year / YEARS_PER_PAGE) * YEARS_PER_PAGE;

// MUI fixes its calendar at DIALOG_WIDTH x VIEW_HEIGHT (header included), from
// internals/constants/dimensions. Matching them here stops the popup resizing
// when the year grid replaces the calendar.
const VIEW_WIDTH = 320;
const VIEW_HEIGHT = 336;

// MUI's free YearCalendar is a scrolling list of every year between minDate and
// maxDate, so a "2020 - 2031" header could never match what was on screen. This
// is a paginated replacement: exactly twelve years per page, moved horizontally
// by the arrows, with the header always naming the page it is showing. It is
// rendered in place of the calendar through the `layout` slot, so MUI's own year
// view is never used and the caller's minDate/maxDate stay untouched.
function YearGrid({ pageStart, onPageShift, onPick, onBack, selectedYear, minYear, maxYear }) {
  const { t } = useLanguage();
  const years = Array.from({ length: YEARS_PER_PAGE }, (_, i) => pageStart + i);
  const prevDisabled = minYear != null && pageStart - 1 < minYear;
  const nextDisabled = maxYear != null && pageStart + YEARS_PER_PAGE > maxYear;

  return (
    <Box
      sx={{
        width: VIEW_WIDTH,
        height: VIEW_HEIGHT,
        display: "flex",
        flexDirection: "column",
        px: 1,
        pt: 1,
        pb: 1.5,
        boxSizing: "border-box",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ minHeight: 40 }}>
        <IconButton size="small" disabled={prevDisabled} onClick={() => onPageShift(-1)} aria-label={t("date.earlierYears")}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.25}
          onClick={onBack}
          sx={{ cursor: "pointer", userSelect: "none" }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {pageStart} - {pageStart + YEARS_PER_PAGE - 1}
          </Typography>
          <ArrowDropDownIcon fontSize="small" sx={{ transform: "rotate(180deg)" }} />
        </Stack>
        <IconButton size="small" disabled={nextDisabled} onClick={() => onPageShift(1)} aria-label={t("date.laterYears")}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box
        sx={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gridTemplateRows: "repeat(3, 1fr)",
          gap: 0.75,
          mt: 1,
          alignContent: "start",
        }}
      >
        {years.map((y) => {
          const outOfRange = (minYear != null && y < minYear) || (maxYear != null && y > maxYear);
          const selected = y === selectedYear;
          return (
            <Button
              key={y}
              size="small"
              disabled={outOfRange}
              onClick={() => onPick(y)}
              variant={selected ? "contained" : "text"}
              sx={{
                minWidth: 0,
                height: "100%",
                borderRadius: "6px",
                fontWeight: selected ? 700 : 500,
                ...(selected
                  ? null
                  : {
                      "&:hover": { backgroundColor: (t) => alpha(t.palette.primary.main, 0.18) },
                    }),
              }}
            >
              {y}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
}

export default function AppDatePicker({
  label,
  value,
  onChange,
  minDate,
  maxDate,
  size = "small",
  fullWidth = false,
  disabled = false,
  required = false,
  error = false,
  helperText,
  sx,
  slotProps,
  ...rest
}) {
  const { isGreek, t } = useLanguage();
  // MUI is only ever given the month and day views; the year level is our own
  // paginated grid (YearGrid), swapped in through the `layout` slot. That keeps
  // MUI's scrolling YearCalendar out of the picture without touching the
  // caller's minDate/maxDate, which the alternative (windowing them per view)
  // would have overridden — and Optimize relies on maxDate to cap the horizon.
  const [view, setView] = useState("day");
  const [yearPage, setYearPage] = useState(null); // non-null = year grid shown

  const selectedDate = toDate(value);
  const minYear = toDate(minDate) ? getYear(toDate(minDate)) : null;
  const maxYear = toDate(maxDate) ? getYear(toDate(maxDate)) : null;

  const openYearGrid = () =>
    setYearPage(pageStartFor(getYear(selectedDate || new Date())));

  const pickYear = (year) => {
    onChange?.(toStr(setYear(selectedDate || new Date(), year)));
    setYearPage(null);
    setView("month"); // drill down: year -> month -> day
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={isGreek ? el : enGB}
      localeText={
        isGreek
          ? {
              openDatePickerDialogue: () => t("date.chooseDate"),
              previousMonth: t("date.previous"),
              nextMonth: t("date.next"),
              clearButtonLabel: t("date.clear"),
              todayButtonLabel: t("date.today"),
              cancelButtonLabel: t("date.cancel"),
              okButtonLabel: t("date.ok"),
            }
          : undefined
      }
    >
      <DatePicker
        label={label}
        value={toDate(value)}
        onChange={(next) => {
          // Ignore half-typed dates: only emit once the value parses, so a
          // consumer never receives "Invalid Date".
          if (next && !isValid(next)) return;
          onChange?.(toStr(next));
        }}
        minDate={toDate(minDate) || undefined}
        maxDate={toDate(maxDate) || undefined}
        disabled={disabled}
        format="dd-MM-yyyy"
        views={["month", "day"]}
        openTo="day"
        view={view}
        onViewChange={setView}
        // Reopening always starts on the days again, never on whatever view was
        // last left behind.
        onOpen={() => {
          setView("day");
          setYearPage(null);
        }}
        displayWeekNumber
        showDaysOutsideCurrentMonth
        monthsPerRow={4}
        slots={{
          day: MarketDay,
          calendarHeader: CalendarHeader,
          // Replace the whole view area with the year grid while it is open.
          layout: ({ children, ...layoutProps }) => (
            <PickersLayout {...layoutProps}>
              {yearPage != null ? (
                <YearGrid
                  pageStart={yearPage}
                  onPageShift={(d) => setYearPage((p) => p + d * YEARS_PER_PAGE)}
                  onPick={pickYear}
                  onBack={() => setYearPage(null)}
                  selectedYear={selectedDate ? getYear(selectedDate) : null}
                  minYear={minYear}
                  maxYear={maxYear}
                />
              ) : (
                children
              )}
            </PickersLayout>
          ),
        }}
        slotProps={{
          calendarHeader: {
            onOpenYearGrid: openYearGrid,
            onShiftYears: (years) => {
              const base = toDate(value) || new Date();
              onChange?.(toStr(addYears(base, years)));
            },
          },
          // Consumer slots first, so any *other* slot they pass survives;
          // textField/field are then rebuilt with their overrides merged in.
          // (Spreading the consumer object last would wipe out size/sx/helperText
          // whenever a caller passed only slotProps.textField.InputLabelProps.)
          ...(slotProps || {}),
          textField: {
            size,
            fullWidth,
            required,
            error,
            helperText,
            // A text input defaults to a 20-character intrinsic width, and the
            // picker adds two end icons (clear + calendar) on top of that — so
            // without an explicit width these fields render far wider than the
            // native date input they replaced. 178px fits "dd-MM-yyyy" plus both
            // icons; a caller's own width/minWidth still wins, since their sx is
            // spread afterwards.
            sx: { width: 178, ...sx },
            InputLabelProps: { shrink: true },
            ...(slotProps?.textField || {}),
          },
          // A clear button makes "no filter" reachable, which the native input
          // only offered through browser-specific chrome.
          field: { clearable: true, onClear: () => onChange?.(""), ...(slotProps?.field || {}) },
          desktopPaper: {
            sx: {
              // Last two header cells are Sat/Su under a Monday-first locale.
              // Counting from the end survives the leading week-number label.
              "& .MuiDayCalendar-header > :nth-last-child(-n+2)": { color: "error.main" },
              "& .MuiDayCalendar-weekNumber": {
                color: "text.disabled",
                fontSize: 12,
              },
            },
            ...(slotProps?.desktopPaper || {}),
          },
        }}
        {...rest}
      />
    </LocalizationProvider>
  );
}
